import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import QuizContent from '../shared/QuizContent'
import QuizTitle from '../shared/QuizTitle'
import { QUIZ_OPTIONS_DATA } from '../../constants'

import realGoth from '../../assets/quiz/realistic/goth.mp4'
import realMuse from '../../assets/quiz/realistic/muse.mp4'
import realNerd from '../../assets/quiz/realistic/nerd.mp4'
import realPrincess from '../../assets/quiz/realistic/princess.mp4'
import realStar from '../../assets/quiz/realistic/star.mp4'

import animeGoth from '../../assets/quiz/anime/goth.mp4'
import animeMuse from '../../assets/quiz/anime/muse.mp4'
import animeNerd from '../../assets/quiz/anime/nerd.mp4'
import animePrincess from '../../assets/quiz/anime/princess.mp4'
import animeStar from '../../assets/quiz/anime/star.mp4'

export const CHARACTER_LABELS = {
  goth: 'Goth',
  muse: 'Muse',
  nerd: 'Nerd',
  princess: 'Princess',
  star: 'Star',
}

// Character → underlying step2 body attributes (sent to API).
// API expects exact label values: bodyType ['Slim','Athletic','Average','Voluptuous'],
// breastSize ['Small (B)','Medium (C)','Large (D)','Huge (DD)'], buttSize ['Small','Medium','Athletic','Large'].
// NOTE: mapping below is a placeholder ordered alphabetically (slim → voluptuous) — adjust per design.
const CHARACTER_TO_BODY = {
  goth:     { bodyType: 'Slim',       breastSize: 'Small (B)',  buttSize: 'Small'    },
  muse:     { bodyType: 'Slim',       breastSize: 'Medium (C)', buttSize: 'Medium'   },
  nerd:     { bodyType: 'Athletic',   breastSize: 'Medium (C)', buttSize: 'Athletic' },
  princess: { bodyType: 'Average',    breastSize: 'Large (D)',  buttSize: 'Large'    },
  star:     { bodyType: 'Voluptuous', breastSize: 'Huge (DD)',  buttSize: 'Large'    },
}

export const CHARACTER_MEDIA = {
  Realistic: {
    goth:     { video: realGoth,     poster: null },
    muse:     { video: realMuse,     poster: null },
    nerd:     { video: realNerd,     poster: null },
    princess: { video: realPrincess, poster: null },
    star:     { video: realStar,     poster: null },
  },
  Anime: {
    goth:     { video: animeGoth,     poster: null },
    muse:     { video: animeMuse,     poster: null },
    nerd:     { video: animeNerd,     poster: null },
    princess: { video: animePrincess, poster: null },
    star:     { video: animeStar,     poster: null },
  },
}

const QuizCharacter = ({ onAnswer, onPrevious, selections }) => {
  const { t } = useTranslation()
  const characters = useMemo(() => QUIZ_OPTIONS_DATA.character || [], [])
  // Guard against stale localStorage from older builds (value='one'/'two'/etc.) — fall back to default.
  const savedValue = selections?.character?.value
  const isSavedValid = savedValue && characters.some(c => c.value === savedValue)
  const initialValue = isSavedValid ? savedValue : (characters[1]?.value || characters[0]?.value)
  const [selected, setSelected] = useState(initialValue)
  const trackRef = useRef(null)

  const style = selections?.welcome === 'Anime' ? 'Anime' : 'Realistic'
  const mediaForStyle = CHARACTER_MEDIA[style] || CHARACTER_MEDIA.Realistic

  useEffect(() => {
    if (!trackRef.current) return
    const card = trackRef.current.querySelector(`[data-character="${selected}"]`)
    if (card && typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }
    // Run only once on mount; subsequent selection clicks shouldn't auto-scroll
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSelect = useCallback(value => {
    setSelected(value)
  }, [])

  const handleArrow = useCallback(direction => {
    if (!trackRef.current) return
    const track = trackRef.current
    const card = track.querySelector('.quiz-character-card')
    const step = card ? card.getBoundingClientRect().width + 16 : 200
    track.scrollBy({ left: direction * step, behavior: 'smooth' })
  }, [])

  const handleContinue = useCallback(() => {
    if (!selected) return
    const media = mediaForStyle[selected]
    const body = CHARACTER_TO_BODY[selected] || null
    onAnswer('character', {
      value: selected,
      video: media?.video || null,
      poster: media?.poster || null,
      style,
      label: CHARACTER_LABELS[selected] || selected,
      bodyType: body?.bodyType || null,
      breastSize: body?.breastSize || null,
      buttSize: body?.buttSize || null,
    })
  }, [selected, mediaForStyle, style, onAnswer])

  const renderCard = (character, layout) => {
    const isSelected = selected === character.value
    const className = `quiz-character-card ${isSelected ? 'selected' : ''} layout-${layout}`
    const media = mediaForStyle[character.value]
    return (
      <div
        key={`${layout}-${character.value}`}
        data-character={character.value}
        className={className}
        onClick={() => handleSelect(character.value)}
      >
        <div className='quiz-character-card-image'>
          <video
            src={media?.video}
            poster={media?.poster}
            autoPlay
            muted
            loop
            playsInline
            preload='metadata'
            controls={false}
            disablePictureInPicture
            controlsList='nodownload nofullscreen noremoteplayback'
          />
        </div>
        <div className='quiz-character-card-label'>
          {CHARACTER_LABELS[character.value] || character.value}
        </div>
      </div>
    )
  }

  return (
    <div className='quizContentWrapper quizContentWrapperCharacter'>
      <QuizContent className='quiz-character-design'>
        <QuizTitle>{t('choose_your_character')}</QuizTitle>

        <div className='quiz-character-grid'>
          {characters.map(c => renderCard(c, 'grid'))}
        </div>

        <div className='quiz-character-carousel'>
          <button
            type='button'
            className='quiz-character-arrow prev'
            onClick={() => handleArrow(-1)}
            aria-label={t('previous')}
          >
            ‹
          </button>
          <div ref={trackRef} className='quiz-character-track'>
            {characters.map(c => renderCard(c, 'carousel'))}
          </div>
          <button
            type='button'
            className='quiz-character-arrow next'
            onClick={() => handleArrow(1)}
            aria-label={t('next')}
          >
            ›
          </button>
        </div>

        <div className='navigation-wrapper'>
          <button
            className='previous-button quiz-back-button'
            onClick={onPrevious}
            aria-label={t('previous')}
          >
            <span className='quiz-back-icon'>‹</span>
            <span className='quiz-back-text'>{t('previous')}</span>
          </button>
          <button
            className='continue-button'
            onClick={handleContinue}
            disabled={!selected}
          >
            Next
          </button>
        </div>
      </QuizContent>
    </div>
  )
}

export default QuizCharacter
